import * as React from 'react';
import AdminUpgradeStatsContainer from 'admin/components/AdminUpgradeStats';
import AdminUpgradeFeatures from 'admin/components/AdminUpgradeFeatures/AdminUpgradeFeatures';
import { ApolloError } from 'apollo-client';
import assertions from 'lib/assertions';
import Button from 'components/Button';
import Columns2 from 'components/Columns2';
import {
  conditionallyAddPlus,
  trimWhitespaceOnStrings,
  getMarketingExperimentFields,
} from 'utils/selfServeHelpers';
import CREATE_SELF_SERVE_QUOTE_MUTATION from 'admin/mutations/CreateSelfServeQuote.gql';
import deepMap from 'deep-map';
// @ts-ignore
import Countries, { regionsForCountry, stateIsValid, countryIsValid } from 'lib/geo/Country';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Input from 'components/Input';
// @ts-ignore
import InputValueConstants from 'constants/InputValueConstants';
import Link from 'components/Link';
import { Mutation } from 'react-apollo';
import Notice from 'components/Notice';
// @ts-ignore
import SchoolOrDistrictPicker from 'components/SchoolOrDistrictPicker/SchoolOrDistrictPicker';
import { OrganizationType } from 'globalTypes';
import PhoneNumberMaskedInput from 'components/PhoneNumberMaskedInput';
// @ts-ignore
import { REQUESTER_ROLE_OPTIONS } from 'admin/constants/AdminUpgrade';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import { UpgradePlanData, UpgradePlanQuote } from 'admin/components/AdminUpgradePlan/AdminUpgradePlanContainer';
import { baseTrackingPrefix } from 'constants/SelfServeConstants';

import '../styles/AdminUpgradePlanOrganizationInfoForm.scss';

type Props = {
  onPersistState: (fields: UpgradePlanData, quote: UpgradePlanQuote, onSuccess: Function) => void,
  onAdvance: () => void,
  onNoOrgStats: () => void;
  onSelectSchool?: (uuid: String) => void,
  initialOrgUuid?: string;
  displayOrgStats?: string;
  orgTypeString?: string,
};

type State = {
  country?: string;
  state?: string;
  postalCode?: string;
  city?: string;
  streetAddress?: string;
  orgUuid?: string;
  orgType?: OrganizationType;
  lat?: number;
  long?: number;
  teachersCount?: number;
  studentsCount?: number;
  comment?: string;
  requesterFirstName?: string;
  requesterLastName?: string;
  role?: string;
  phoneNumber?: string;
  requesterEmail?: string;
  organizationHasAddress: boolean;
};

class AdminUpgradePlanOrganizationInfoForm extends React.Component<Props, State> {
  readonly state: State;
  private readonly trackingPrefix = `${baseTrackingPrefix}.organization_info_page`
  private readonly onscreenFields: (keyof State)[] = [
    'orgUuid',
    'teachersCount',
    'studentsCount',
    'comment',
    'requesterFirstName',
    'requesterLastName',
    'role',
    'phoneNumber',
    'requesterEmail',
  ];

  constructor(props: Props) {
    super(props);
    this.state = {
      orgUuid: props.initialOrgUuid,
      organizationHasAddress: true,
    };
  }

  componentDidMount() {
    this.track('view');
  }

  track = (key: string) => {
    const organization_uuid = this.state.orgUuid || null;
    EventTracking.trackKey(`${this.trackingPrefix}.${key}`,
      {
        organization_uuid,
      },
    );
  };

  canSubmit = () => {
    return this.onscreenFields
      .map(k => this.state[k])
      .every(v => v !== undefined)
  };

  createSelfServeQuoteInputs = () => {
    const {
      orgUuid,
      studentsCount,
      teachersCount,
      requesterFirstName,
      requesterLastName,
      requesterEmail,
      phoneNumber,
      role,
      lat,
      long,
      streetAddress,
      city,
      state,
      postalCode,
      country,
    } = this.state;

    // Automatically set the start date to one day after the day the
    // org info form is filled out and submitted
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1);

    return deepMap(
      {
        organizationUuid: orgUuid,
        studentsCount: studentsCount,
        teachersCount: teachersCount,
        startDate: startDate,
        organizationAddress: {
          latitude: lat || null,
          longitude: long || null,
          streetAddress: streetAddress,
          city: city,
          state: state,
          country: country,
          postalCode: postalCode,
        },
        requesterFirstName: requesterFirstName,
        requesterLastName: requesterLastName,
        requesterEmail: requesterEmail,
        requesterPhoneNumber: conditionallyAddPlus(phoneNumber),
        requesterRole: role,
        billingFirstName: requesterFirstName,
        billingLastName: requesterLastName,
        billingEmail: requesterEmail,
        billingPhoneNumber: conditionallyAddPlus(phoneNumber),
        taxExempt: undefined, // Tax exemption status is determined in api
        lastScreenName: `org_info`,
        ...getMarketingExperimentFields(),
      },
      trimWhitespaceOnStrings,
      { inPlace: true },
    );
  };

  renderSidebar() {
    const { displayOrgStats, initialOrgUuid, onNoOrgStats } = this.props;

    return displayOrgStats && initialOrgUuid ? (
      <AdminUpgradeStatsContainer organizationUuid={initialOrgUuid} onNoStats={onNoOrgStats} />
    ) : (
      <AdminUpgradeFeatures />
    );
  }

  renderSubmitButton = () => {
    const { onPersistState, onAdvance } = this.props;

    return (
      <Mutation
        mutation={CREATE_SELF_SERVE_QUOTE_MUTATION}
        onError={(error: ApolloError) => {
          this.track('self_serve_quote_creation.error');
          ToastActions.showErrorToast(error.message)}
        }
        onCompleted={(results: any) => {
          const quote = results?.createQuote?.quote;
          const { organizationHasAddress, ...upgradePlanData } = this.state;
          this.track('self_serve_quote_creation.success');
          onPersistState(
            upgradePlanData as UpgradePlanData,
            quote as UpgradePlanQuote,
            onAdvance,
          );
        }}>
        {(createSelfServeQuote: Function) => (
          <Button
            disabled={!this.canSubmit()}
            onClick={() => {
              this.track('submit_button.click')
              if (this.canSubmit()) {
                createSelfServeQuote({
                  variables: { input: this.createSelfServeQuoteInputs() },
                });
              }
            }}>
            Submit
          </Button>
        )}
      </Mutation>
    );
  }

  renderOrganizationPickerForm = () => {
    const { onSelectSchool, orgTypeString } = this.props;
    return (<FormElement
      label={'Organization name'}
      onValidChange={o => {
        this.track('select_school.click');
        if (o.country && countryIsValid(o.country)) {
          this.setState({ country: o.country });
        }
        if (o.state && stateIsValid(o.state, o.country)) {
          this.setState({ state: o.state });
        }
        if (o.city) {
          this.setState({ city: o.city });
        }

        if (o.street) {
          this.setState({ streetAddress: o.street });
        }

        if (o.zip) {
          this.setState({ postalCode: o.zip });
        }

        if (o.type) {
          this.setState({ orgType: o.type });
        }
        this.setState({
          orgUuid: o.uuid,
          lat: o.latitude,
          long: o.longitude,
        });
        this.setState({
          organizationHasAddress:
            Boolean(o.country) &&
            Boolean(o.state) &&
            Boolean(o.city) &&
            Boolean(o.street) &&
            Boolean(o.zip),
        });
        if (orgTypeString === 'school' && onSelectSchool) {
          onSelectSchool(o.uuid);
        }
      }}
      element={SchoolOrDistrictPicker}
      elementProps={{
        type: orgTypeString,
        onClickInteraction: () => this.track('school_picker.click'),
        initialOrganizationUuid: this.props.initialOrgUuid,
      }}
    />);
  }

  renderAddressForm = () => {
    const regionLabel = regionsForCountry[this.state.country] ? regionsForCountry[this.state.country].label : 'Region';
    return (
      <div>
        <FormElement
          label="Country"
          validate={value => {
            assertions.notEmpty(value, 'Please select a country');
          }}
          onValidChange={country => this.setState({ country: country })}
          onInvalidChange={() => this.setState({ country: undefined })}
          element={Select}
          elementProps={{
            name: 'country',
            value: this.state.country,
            options: Countries,
            className: 'AdminUpgradePlanOrganizationInfoForm-countryPicker',
          }}
        />
        <Columns2>
          <FormElement
            label={
              <span>
                {this.props.orgTypeString
                  ? `${this.props.orgTypeString} street address`
                  : 'Street address'}{' '}
              </span>
            }
            validate={address => {
              assertions.notEmpty(address);
            }}
            onValidChange={e => this.setState({ streetAddress: e.target.value })}
            onInvalidChange={() => this.setState({ streetAddress: undefined })}
            element={Input}
            elementProps={{
              defaultValue: this.state.streetAddress,
              placeholder: '1234 Remind Road',
              'data-test-selector': 'street-address-input',
            }}
          />
          <FormElement
            label="City"
            validate={city => {
              assertions.notEmpty(city);
            }}
            onValidChange={e => this.setState({ city: e.target.value })}
            onInvalidChange={() => this.setState({ city: undefined })}
            element={Input}
            elementProps={{
              value: this.state.city,
              'data-test-selector': 'city-input',
            }}
          />
        </Columns2>
        <Columns2>
          <FormElement
            label={regionLabel}
            onValidChange={state => this.setState({ state: state })}
            onInvalidChange={() => this.setState({ state: undefined })}
            validate={value => {
              assertions.notEmpty(value, `Please select a ${regionLabel.toLowerCase()}`);
            }}
            element={Select}
            elementProps={{
              name: 'state',
              value: this.state.state,
              options: regionsForCountry[this.state.country] ? regionsForCountry[this.state.country].regions : [],
              disabled: !this.state.country,
              className: 'AdminUpgradePlanOrganizationInfoForm-regionPicker',
            }}
          />
          <FormElement
            label="Postal code"
            validate={postalCode => {
              assertions.notEmpty(postalCode);
            }}
            onValidChange={e => this.setState({ postalCode: e.target.value })}
            onInvalidChange={() => this.setState({ postalCode: undefined })}
            element={Input}
            elementProps={{
              defaultValue: this.state.postalCode,
              'data-test-selector': 'postal-code-input',
            }}
          />
        </Columns2>
      </div>);
  }

  renderAboutOrgForm = () => {
    return (
      <div>
        <Row>
          <Text weight="semibold" size="xlarge" element="div">
            Tell us about your organization
          </Text>
          <Text size="medium" element="div">
            Find or add your school, district, university, or other organization.
          </Text>
        </Row>
        {this.renderOrganizationPickerForm()}
        {!this.state.organizationHasAddress && this.renderAddressForm()}
        <Row size="large">
          <FormElement
            label={<span>Why are you interested in the Remind plan?</span>}
            onValidChange={e => this.setState({ comment: e.target.value })}
            onInvalidChange={() => this.setState({ comment: undefined })}
            element={Input}
            elementProps={{
              defaultValue: this.state.comment,
              onFocus: () => this.track('details.click'),
              placeholder:
                'Let us know why you\'re considering Remind and any questions you might have.',
            }}
          />
        </Row>
        <Columns2>
          <FormElement
            label="Estimated teachers on staff"
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
            }}
            onValidChange={e => this.setState({ teachersCount: e.target.value })}
            onInvalidChange={() => this.setState({ teachersCount: undefined })}
            element={Input}
            elementProps={{
              onFocus: () => this.track('teachers_on_staff.click'),
              defaultValue: this.state.teachersCount,
              'data-test-selector': 'est-teachers-input',
            }}
          />
          <FormElement
            label="Estimated student enrollment"
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
              assertions.valueGreaterThan(
                5,
                e.target.value,
                'Must include more than five people',
              );
            }}
            onValidChange={e => this.setState({ studentsCount: e.target.value })}
            onInvalidChange={() => this.setState({ studentsCount: undefined })}
            element={Input}
            elementProps={{
              onFocus: () => this.track('student_enrollment.click'),
              defaultValue: this.state.studentsCount,
              'data-test-selector': 'est-students-input',
            }}
          />
        </Columns2>
      </div>
      );
  }

  renderAboutYouSection = () => {
    return (
      <section className="AdminUpgradePlanOrganizationInfoForm-about">
        <Row size="large">
          <Heading level="2" levelStyle="5">
            <Text size="medium" weight="light" color="eclipse">
              About you
            </Text>
          </Heading>
        </Row>
        <Columns2>
          <FormElement
            label="First name"
            validate={e => {
              assertions.notEmpty(e.target.value);
              assertions.lengthLesserThanOrEqualTo(
                e.target.value,
                InputValueConstants.FIRST_NAME_LENGTH,
              );
            }}
            onValidChange={e => this.setState({ requesterFirstName: e.target.value })}
            onInvalidChange={() => this.setState({ requesterFirstName: undefined })}
            element={Input}
            elementProps={{
              onFocus: () => this.track('requester_first_name.click'),
              'data-test-selector': 'first-name-input',
            }}
          />
          <FormElement
            label="Last name"
            validate={e => {
              assertions.notEmpty(e.target.value);
              assertions.lengthLesserThanOrEqualTo(
                e.target.value,
                InputValueConstants.LAST_NAME_LENGTH,
              );
            }}
            onValidChange={e => this.setState({ requesterLastName: e.target.value })}
            onInvalidChange={() => this.setState({ requesterLastName: undefined })}
            element={Input}
            elementProps={{
              onFocus: () => this.track('requester_last_name.click'),
              'data-test-selector': 'last-name-input',
            }}
          />
        </Columns2>
        <Columns2>
          <FormElement
            label="Role"
            validate={role => {
              assertions.notEmpty(role);
            }}
            onValidChange={role => {
              this.setState({ role: role });
            }}
            onInvalidChange={() => this.setState({ role: undefined })}
            element={Select}
            elementProps={{
              onFocus: () => this.track('requester_role.click'),
              options: REQUESTER_ROLE_OPTIONS,
              placeholder: 'Select...',
              value: this.state.role,
              className: 'AdminUpgradePlanOrganizationInfoForm-rolePicker',
            }}
          />
        </Columns2>
        <Columns2>
          <FormElement
            label="Phone number"
            validate={e => {
              assertions.number(e.raw, 'Invalid phone number.');
              assertions.completePhoneNumber(e.raw, 'Invalid phone number.');
            }}
            onValidChange={e => this.setState({ phoneNumber: e.raw })}
            onInvalidChange={() =>
              this.setState({ phoneNumber: undefined })
            }
            element={PhoneNumberMaskedInput}
            elementProps={{
              onFocus: () => this.track('requester_phone_number.click'),
              'data-test-selector': 'phone-number-input',
            }}
          />
          <FormElement
            label="Work email address"
            validate={e => {
              assertions.email(e.target.value);
              assertions.notEmpty(e.target.value);
            }}
            onValidChange={e => {
              this.setState({ requesterEmail: e.target.value });
            }}
            onInvalidChange={() =>
              this.setState({ requesterEmail: undefined })
            }
            element={Input}
            elementProps={{
              onFocus: () => this.track('requester_email.click'),
              'data-test-selector': 'email-input',
            }}
          />
        </Columns2>
        <Columns2>
          {this.renderSubmitButton()}
        </Columns2>
      </section>
    );
  }

  renderLowStudentCountWarning = () => {
    return (
      <Row size="xxxlarge">
        <Notice color="polar-bear" noShadow data-test="student-enrollment-too-small">
          <Text size="xsmall">
            Remind plans are only available for schools and districts, but you can always start
            with an individual account! Click <Link to="/">here</Link> to sign up for free.
          </Text>
        </Notice>
      </Row>
    );
  }

  render() {
    return (
      <div className="AdminUpgradePlanOrganizationInfoForm">
        <div className="AdminUpgradePlanOrganizationInfoForm-content">
          <div className="AdminUpgradePlanOrganizationInfoForm-content-body">
            <div className="AdminUpgradePlanOrganizationInfoForm-content-body-forms">
              {this.renderAboutOrgForm()}
              {this.state.studentsCount && this.state.studentsCount <= 5 && this.renderLowStudentCountWarning()}
              {this.renderAboutYouSection()}
            </div>
            <div className="AdminUpgradePlanOrganizationInfoForm-content-body--desktop-side">
              <Row size="xxxlarge">{this.renderSidebar()}</Row>
            </div>
          </div>
        </div>
        <div className="AdminUpgradePlanOrganizationInfoForm--mobile-bottom">
          <div className="AdminUpgradePlanOrganizationInfoForm-info-container">
            {this.renderSidebar()}
          </div>
        </div>
      </div>
    );
  }
}


export { AdminUpgradePlanOrganizationInfoForm };
export default AdminUpgradePlanOrganizationInfoForm;
